import { useState } from 'react'
import { auroraStore } from '../../store/auroraStore'
import type { PlanBlock, PlanWeek } from '../../store/selectors'
import { addBlock, moveBlock, setBlockStatus, setWeekTheme } from '../../lib/planEdit'
import { formatDuration, formatFriendlyDate, todayISO } from '../../lib/dates'
import { BlockEditor } from './BlockEditor'
import { EmptyState } from '../ui/EmptyState'

/**
 * One week, day by day, editable in place.
 *
 * This is where a parent rearranges reality: the morning got away from them,
 * so the block moves down; she is sick of the sight of sight words, so it comes
 * off. Every control here writes straight through to the plan and every write
 * is undoable, which is what makes it safe to touch.
 */
export function WeekView({ week, onBack }: { week: PlanWeek; onBack?: () => void }) {
  const today = todayISO()
  const [editing, setEditing] = useState<{ date: string; block: PlanBlock } | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [theme, setTheme] = useState(week.theme ?? '')

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  const openDay = (date: string) => {
    const s = auroraStore.getState() as any
    s.selectDate(date)
    s.setView('today')
  }

  return (
    <div className="week">
      <header className="week-head">
        {onBack && (
          <button className="week-back" onClick={onBack}>
            <BackIcon /> Cycle
          </button>
        )}

        {renaming ? (
          <form
            className="week-rename"
            onSubmit={(e) => {
              e.preventDefault()
              setWeekTheme(week.number, theme.trim())
              setRenaming(false)
            }}
          >
            <input
              className="field-input"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              aria-label="Week theme"
              autoFocus
            />
            <button className="btn btn-primary btn-sm" type="submit">
              Save
            </button>
          </form>
        ) : (
          <button className="week-theme" onClick={() => setRenaming(true)}>
            <span className="week-number">Week {week.number}</span>
            <span className="week-theme-name">{week.theme ?? 'Untitled week'}</span>
            <PencilIcon />
          </button>
        )}
      </header>

      {week.focus.length > 0 && (
        <ul className="week-focus">
          {week.focus.map((f) => (
            <li key={f.domain} style={{ '--domain-color': `var(--domain-${f.domain})` } as React.CSSProperties}>
              <span className="week-focus-domain">{f.domain}</span>
              <span>{f.statement}</span>
            </li>
          ))}
        </ul>
      )}

      {week.materials.length > 0 && (
        <details className="week-materials">
          <summary>
            Gather before Sunday
            <span className="tnum"> · {week.materials.length}</span>
          </summary>
          <ul>
            {week.materials.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </details>
      )}

      {week.days.length === 0 ? (
        <EmptyState
          title="This week is still empty"
          body="Ask Aurora to fill it in — it will use everything it has learned so far."
          action={{
            label: 'Fill in this week',
            onClick: () => ask(`Fill in week ${week.number} of the plan`),
          }}
        />
      ) : (
        <div className="week-days">
          {week.days.map((day) => (
            <section
              key={day.date}
              className={`week-day${day.date === today ? ' is-today' : ''}`}
              aria-current={day.date === today ? 'date' : undefined}
            >
              <header className="week-day-head">
                <button className="week-day-date" onClick={() => openDay(day.date)}>
                  {day.date === today ? 'Today' : formatFriendlyDate(day.date)}
                </button>
                <span className="week-day-total tnum">
                  {day.blocks.length
                    ? formatDuration(day.blocks.reduce((n, b) => n + (b.minutes ?? 10), 0))
                    : 'open'}
                </span>
              </header>

              {day.note && <p className="week-day-note">{day.note}</p>}

              <ul className="week-blocks">
                {day.blocks.map((block, i) => {
                  const settled = block.status && block.status !== 'pending'
                  return (
                    <li
                      key={block.id}
                      className={`week-block${settled ? ' is-settled' : ''}`}
                      style={
                        { '--domain-color': domainColor(block.domain) } as React.CSSProperties
                      }
                    >
                      <button
                        className="week-block-check"
                        onClick={() =>
                          setBlockStatus(
                            day.date,
                            block.id,
                            block.status === 'done' ? 'pending' : 'done',
                            block.title,
                          )
                        }
                        aria-pressed={block.status === 'done'}
                        aria-label={
                          block.status === 'done'
                            ? `Reopen ${block.title}`
                            : `Mark ${block.title} done`
                        }
                      >
                        {block.status === 'done' && <CheckIcon />}
                        {block.status === 'skipped' && <SkipIcon />}
                      </button>

                      <button
                        className="week-block-main"
                        onClick={() => setEditing({ date: day.date, block })}
                      >
                        <span className="week-block-title">{block.title}</span>
                        <span className="week-block-meta tnum">
                          {block.domain ? `${block.domain} · ` : ''}
                          {formatDuration(block.minutes ?? 10)}
                        </span>
                      </button>

                      <span className="week-block-order">
                        <button
                          onClick={() => moveBlock(day.date, block.id, -1)}
                          disabled={i === 0}
                          aria-label={`Move ${block.title} earlier`}
                        >
                          <ChevronIcon up />
                        </button>
                        <button
                          onClick={() => moveBlock(day.date, block.id, 1)}
                          disabled={i === day.blocks.length - 1}
                          aria-label={`Move ${block.title} later`}
                        >
                          <ChevronIcon />
                        </button>
                      </span>
                    </li>
                  )
                })}
              </ul>

              <button
                className="week-add"
                onClick={() =>
                  addBlock(day.date, {
                    title: 'New activity',
                    why: 'Say what this is for.',
                    minutes: 10,
                  })
                }
              >
                <PlusIcon /> Add something
              </button>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <BlockEditor
          date={editing.date}
          block={editing.block}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

const KNOWN = new Set([
  'literacy', 'language', 'logic', 'math', 'fineMotor', 'grossMotor',
  'socialEmotional', 'executiveFunction', 'creative', 'time', 'science',
  'secondLanguage', 'selfCare',
])
function domainColor(domain?: string): string {
  return domain && KNOWN.has(domain) ? `var(--domain-${domain})` : 'var(--line-strong)'
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
}
const CheckIcon = () => <svg {...S} width="12" height="12" strokeWidth={2.75}><path d="M4 12.5l5 5L20 6.5" /></svg>
const SkipIcon = () => <svg {...S} width="11" height="11"><path d="M6 6l12 12M18 6L6 18" /></svg>
const PlusIcon = () => <svg {...S} width="14" height="14"><path d="M12 5v14M5 12h14" /></svg>
const BackIcon = () => <svg {...S} width="15" height="15"><path d="M15 6l-6 6 6 6" /></svg>
const PencilIcon = () => (
  <svg {...S} width="13" height="13" strokeWidth={1.75}>
    <path d="M4 20h4L20 8l-4-4L4 16z" />
  </svg>
)
const ChevronIcon = ({ up }: { up?: boolean }) => (
  <svg {...S} width="13" height="13" style={up ? { transform: 'rotate(180deg)' } : undefined}>
    <path d="M6 9l6 6 6-6" />
  </svg>
)
